import { Icon } from '@buggy/shared'
import { Button, Cascader, CascaderProps, Flex, Tooltip } from 'antd'
import type { DefaultOptionType } from 'antd/es/cascader'
import type { BaseOptionType } from 'antd/es/select'
import type { FunctionComponent } from 'react'
import styled from 'styled-components'
import { useIntl } from '@/i18n/hooks/use-intl'
import type { Optional } from '@/types/optional'
import type { CollaborativeItemType } from '@/types/schema/collaborative/collaborative-item'
import {
  convertPlatformShortcode,
  PlatformShortcode,
} from '@/utils/convert-platform'
import getCooperationItemI18nKey from '@/utils/get-collaborative-item-i18n-key'

export interface Option extends BaseOptionType {
  platform: PlatformShortcode
  items: (CollaborativeItemType | 'custom')[]
}

export interface CollaborativeItemTypeCascaderProps
  extends Omit<
    CascaderProps,
    'options' | 'onChange' | 'expandIcon' | 'multiple' | 'value' | 'suffixIcon'
  > {
  availableOptions?: Option[]
  disabledOptions?: Option[]
  value?: [PlatformShortcode, CollaborativeItemType | 'custom']
  onChange?: (
    value: [PlatformShortcode, CollaborativeItemType | 'custom'],
  ) => void
}

const DEFAULT_OPTIONS: Option[] = [
  {
    platform: PlatformShortcode.Instagram,
    items: [
      'post',
      'live',
      'reels',
      'story',
      'ad_authorization',
      'material_authorization',
      'custom',
    ],
  },
  {
    platform: PlatformShortcode.Facebook,
    items: [
      'post',
      'video',
      'live',
      'story',
      'shorts',
      'ad_authorization',
      'material_authorization',
      'custom',
    ],
  },
  {
    platform: PlatformShortcode.YouTube,
    items: [
      'post',
      'video',
      'live',
      'shorts',
      'ad_authorization',
      'material_authorization',
      'custom',
    ],
  },
  {
    platform: PlatformShortcode.Twitter,
    items: ['post', 'ad_authorization', 'material_authorization', 'custom'],
  },
  {
    platform: PlatformShortcode.TikTok,
    items: [
      'post',
      'shorts',
      'ad_authorization',
      'material_authorization',
      'custom',
    ],
  },
  {
    platform: PlatformShortcode.Other,
    items: ['custom'],
  },
]

const CollaborativeItemTypeCascader: FunctionComponent<
  CollaborativeItemTypeCascaderProps
> = ({
  availableOptions = DEFAULT_OPTIONS,
  disabledOptions,
  value,
  onChange,
  children,
  ...props
}) => {
  const { formatMessage } = useIntl()

  const options = availableOptions.map((option): Optional<BaseOptionType> => {
    const platform = convertPlatformShortcode(option.platform)
    const disabledItems = disabledOptions?.find(
      (disabledOption) => disabledOption.platform === option.platform,
    )?.items

    return {
      value: option.platform,
      label: (
        <Flex align='center' gap={4} style={{ display: 'inline-flex' }}>
          {platform.sharedIconName && <Icon name={platform.sharedIconName} />}
          {option.platform === PlatformShortcode.Other
            ? formatMessage({ id: 'general:platform_custom' })
            : platform.title}
        </Flex>
      ),
      children: option.items.map((item) => {
        const disabled =
          value?.[1] === item ? false : disabledItems?.includes(item)
        const i18nId =
          item === 'custom'
            ? 'general:cooperation_item_type_custom'
            : getCooperationItemI18nKey(item)

        return {
          value: item,
          label: (
            <Tooltip
              title={
                disabled
                  ? formatMessage({
                      id: 'campaign:cooperation_detail_duplicated',
                    })
                  : undefined
              }
            >
              {formatMessage({
                id: i18nId,
              })}
            </Tooltip>
          ),
          disabled,
        }
      }),
    }
  })

  return (
    <Cascader
      allowClear={false}
      expandIcon={
        <Flex align='center' justify='center'>
          <Icon fontSize={16} name='chevron-right' />
        </Flex>
      }
      expandTrigger='hover'
      optionRender={(option: DefaultOptionType) =>
        option.value === 'custom' ? (
          <CustomOptionButton
            icon={<Icon name='plus' />}
            size='small'
            type='link'
          >
            {formatMessage({
              id: 'directory:search_fields_setting_drawer_add_customized_field_act',
            })}
          </CustomOptionButton>
        ) : (
          option.label
        )
      }
      options={options}
      placeholder={formatMessage({ id: 'campaign:cooperation_add_act' })}
      size='small'
      suffixIcon={<Icon fontSize={16} name='chevron-down' />}
      value={value}
      onChange={(
        value: NonNullable<CollaborativeItemTypeCascaderProps['value']>,
      ): void => {
        onChange?.(value)
      }}
      {...props}
    >
      {children}
    </Cascader>
  )
}

const CustomOptionButton = styled(Button)`
  &&.ant-btn {
    --ant-control-height-sm: 22px;
    --ant-button-content-line-height-sm: 100%;
    padding: 0;
  }
`

export default CollaborativeItemTypeCascader
